import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';

const TdpVerificationPage = () => {
  const { userToken } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResultByToken = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!userToken) {
          setError('Invalid verification link');
          return;
        }

        const response = await fetch('https://eap.ethernet.edu.et/api/tdp-exam-result-by-token', {
          method: 'POST',
          headers: {
            'X-API-Key': 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_token: userToken
          })
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Certificate not found or invalid token');
          } else if (response.status === 401) {
            throw new Error('Invalid API key');
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          setResult(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch result');
        }
      } catch (err) {
        console.error('Error fetching result by token:', err);
        setError(err.message || 'Failed to load certificate data');
      } finally {
        setLoading(false);
      }
    };

    fetchResultByToken();
  }, [userToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center justify-center py-12">
                <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: 'rgb(18,82,166)' }}></i>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">Verifying Certificate</h2>
                <p className="text-gray-500">Please wait while we verify the certificate...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center justify-center py-12">
                <i className="fa-solid fa-exclamation-triangle text-4xl mb-4 text-red-500"></i>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">Verification Failed</h2>
                <p className="text-gray-500 mb-6">{error}</p>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                >
                  <i className="fa-solid fa-home mr-2"></i>
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center justify-center py-12">
                <i className="fa-solid fa-file-circle-question text-4xl mb-4 text-gray-400"></i>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">No Data Found</h2>
                <p className="text-gray-500 mb-6">No certificate data available for this token.</p>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                >
                  <i className="fa-solid fa-home mr-2"></i>
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const passMark = 70; // TDP pass mark is 70%
  
  // Handle incomplete status
  const isIncomplete = result.total_score === 'incomplete' || result.status === 'Incomplete';
  const percentageScore = isIncomplete ? 'Incomplete' : result.total_score;
  const isPassed = isIncomplete ? false : (percentageScore >= passMark);
  const status = isIncomplete ? 'Incomplete' : (result.status || (isPassed ? 'Pass' : 'Fail'));

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <div>
                <h2 className="text-2xl font-bold flex items-center mb-2" style={{ color: 'rgb(18,82,166)' }}>
                  <i className="fa-solid fa-shield-check mr-3 text-2xl" style={{ color: 'rgb(18,82,166)' }} />
                  TDP Certificate Verification
                </h2>
                <p className="text-gray-600 text-sm">Official TDP examination certificate verification</p>
              </div>
              <div className="flex items-center gap-3 mt-4 sm:mt-0">
                <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <i className="fa-solid fa-check-circle text-green-500 text-lg"></i>
                  <span className="text-sm font-semibold text-green-700">Verified Certificate</span>
                </div>
              </div>
            </div>
          </section>

          {/* Student Info Card */}
          <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' }}>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                <i className="fa-solid fa-user-graduate mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
                Student Information
              </h3>
              <p className="text-sm text-gray-600">Verified student details from the certificate</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'fa-user', label: 'Username', value: result.username },
                { icon: 'fa-id-card', label: 'Full Name', value: result.full_name || 'N/A' },
                { icon: 'fa-venus-mars', label: 'Gender', value: result.gender || 'N/A' },
                { icon: 'fa-building-columns', label: 'Institution', value: result.institution_name || 'N/A' },
                { icon: 'fa-book', label: 'Subject', value: result.subject || 'N/A' },
                // Only show certificate issued date for passed students
                ...(isPassed ? [{ icon: 'fa-calendar-check', label: 'Certificate Issued', value: result.certificate_issued_date || 'N/A' }] : []),
              ].map(({ icon, label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-center mb-2">
                    <i className={`fa-solid ${icon} mr-2 text-sm`} style={{ color: 'rgb(18,82,166)' }}></i>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 break-words">{value}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Results Table */}
          <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr style={{ backgroundColor: 'rgb(18,82,166)' }}>
                    <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Total Score</th>
                    <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Pass Mark</th>
                    <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">
                          {percentageScore}%
                        </div>
                        <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              isIncomplete ? 'bg-yellow-500' : (isPassed ? 'bg-green-600' : 'bg-red-600')
                            }`}
                            style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {passMark}%
                      </div>
                    </td>
                    <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        isIncomplete
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : isPassed 
                          ? 'bg-green-50 text-green-700 border border-green-200' 
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                        {status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden">
              <div className="p-4 sm:p-6">
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-500">Total Score</span>
                      <span className="text-lg font-bold" style={{ color: isIncomplete ? '#d97706' : (isPassed ? '#059669' : '#dc2626') }}>
                        {percentageScore}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          isIncomplete ? 'bg-yellow-500' : (isPassed ? 'bg-green-600' : 'bg-red-600')
                        }`}
                        style={{ width: `${isIncomplete ? 0 : (percentageScore || 0)}%` }}
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-500">Pass Mark</span>
                      <span className="text-sm font-medium text-gray-900">{passMark}%</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Status</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        isIncomplete
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : isPassed 
                          ? 'bg-green-50 text-green-700 border border-green-200' 
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        <i className={`fa-solid ${isIncomplete ? 'fa-clock' : (isPassed ? 'fa-check-circle' : 'fa-times-circle')} mr-1`}></i>
                        {status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Important Note */}
          <section className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <i className="fa-solid fa-info-circle text-blue-600 text-xl"></i>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">Important Note</h3>
                <p className="text-sm text-blue-700 leading-relaxed">
                  This certificate has been successfully verified and is authentic. The information displayed above is the official TDP examination result.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TdpVerificationPage;
